const DAY_START_HOUR = 2;

// Un Intl.DateTimeFormat por timezone, cacheado a nivel módulo: construirlo
// cuesta ~0,1-0,3ms y esta función corre una vez POR PAQUETE en cada
// recompute de las vistas en vivo (los formatters son inmutables y la
// cardinalidad de timezones es mínima).
const fmtCache = new Map();
function getZonedFormatter(tz) {
  let fmt = fmtCache.get(tz);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hour12: false,
    });
    fmtCache.set(tz, fmt);
  }
  return fmt;
}

function getZonedDateParts(date, tz) {
  const fmt = getZonedFormatter(tz);
  const parts = fmt.formatToParts(date).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});
  return {
    year: parseInt(parts.year, 10),
    month: parseInt(parts.month, 10),
    day: parseInt(parts.day, 10),
    hour: parts.hour === '24' ? 0 : parseInt(parts.hour, 10),
  };
}

function getOperatingDayKey(timestamp, tz) {
  const d = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (isNaN(d.getTime())) return null;

  const { year, month, day, hour } = tz
    ? getZonedDateParts(d, tz)
    : { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(), hour: d.getHours() };

  let y = year, m = month, dd = day;
  if (hour < DAY_START_HOUR) {
    const prev = new Date(Date.UTC(y, m - 1, dd));
    prev.setUTCDate(prev.getUTCDate() - 1);
    y = prev.getUTCFullYear();
    m = prev.getUTCMonth() + 1;
    dd = prev.getUTCDate();
  }
  return `${y}-${String(m).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
}

function getRelevantTimestamp(pkg) {
  if (pkg.status === 4) return pkg.deliveredAt || pkg.lastStatusChange;
  if (pkg.status === 6 || pkg.status === 7) return pkg.lastFailedAt || pkg.lastStatusChange;
  return pkg.lastStatusChange;
}

export function filterPackagesByOperatingDay(packages, dateStr, tz) {
  return packages.filter((pkg) => {
    if (pkg.status === 3 || pkg.status === 30) return true;
    const ts = getRelevantTimestamp(pkg);
    if (!ts) return true;
    if (!dateStr) return true;
    return getOperatingDayKey(ts, tz) === dateStr;
  });
}

/**
 * Offset del timezone respecto a UTC, en ms, VIGENTE en ese instante (o sea,
 * con el DST del día correcto, no el de hoy).
 */
function getTzOffsetMs(date, tz) {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const p = fmt.formatToParts(date).reduce((acc, part) => {
    if (part.type !== 'literal') acc[part.type] = part.value;
    return acc;
  }, {});
  const asIfUtc = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    p.hour === '24' ? 0 : Number(p.hour),
    Number(p.minute),
    Number(p.second),
  );
  return asIfUtc - date.getTime();
}

/** Instante UTC en el que el reloj de `tz` marca `y-m-d h:00`. */
function zonedWallTimeToUtc(y, m, d, h, tz) {
  const guess = Date.UTC(y, m - 1, d, h);
  // Dos pasadas: la primera estima el offset con el instante equivocado; la
  // segunda lo recalcula ya parado (casi) en el instante correcto, que es lo
  // que hace falta para no errar por una hora en los saltos de horario.
  const first = guess - getTzOffsetMs(new Date(guess), tz);
  return guess - getTzOffsetMs(new Date(first), tz);
}

/**
 * Ventana UTC [from, to] del DÍA OPERATIVO `dateStr` en `tz`.
 *
 * Usa el MISMO corte de las 2am que `filterPackagesByOperatingDay`, y ese
 * acople es el punto: si la traza GPS se pidiera por día natural del navegador,
 * el mapa y la lista de paradas estarían hablando de días distintos —el
 * repartidor que entregó a las 21:30 aparecía sin recorrido porque su ventana
 * arrancaba a medianoche del navegador (en UTC, cuatro horas antes que en
 * Santiago) y sus reportes caían fuera.
 *
 * `to` se acota a `now` cuando el día todavía no terminó: pedir el futuro no
 * agrega nada y ensancha la consulta al back.
 *
 * @param {string} dateStr  'YYYY-MM-DD' del día operativo.
 * @param {string} tz       Timezone IANA de la operación.
 * @param {Date}   [now]    Inyectable para tests.
 * @returns {{from: Date, to: Date} | null}  null si `dateStr` no es válido.
 */
export function getOperatingDayRange(dateStr, tz, now = new Date()) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr || ''));
  if (!match) return null;
  const [, y, m, d] = match.map(Number);
  const zone = tz || 'UTC';
  const fromMs = zonedWallTimeToUtc(y, m, d, DAY_START_HOUR, zone);
  const toMs = zonedWallTimeToUtc(y, m, d + 1, DAY_START_HOUR, zone);
  return { from: new Date(fromMs), to: new Date(Math.min(toMs, now.getTime())) };
}

export { DAY_START_HOUR, getZonedDateParts };
